import Resizer from 'react-image-file-resizer';
import heic2any from 'heic2any';

/**
 * Normalizes HEIC/HEIF files by ensuring they have the correct MIME type
 * @param file - The file to normalize
 * @returns The normalized file with correct MIME type if it was HEIC/HEIF
 */
export function normalizeHeicFile(file: File): File {
	const looksHeic = /\.(heic|heif)$/i.test(file.name);
	if (!file.type && looksHeic) {
		// Rewrap with the correct MIME so heic2any will accept it
		return new File([file], file.name, { type: 'image/heic' });
	}
	// Some Windows setups may prefer image/heif — you can try that too:
	// return new File([file], file.name, { type: "image/heif" });
	return file;
}

/**
 * Validates if a file is a valid image format
 * @param file - The file to validate
 * @returns Promise<boolean> - true if valid image format
 */
export async function isValidImageFormat(file: File): Promise<boolean> {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => resolve(true);
		img.onerror = () => resolve(false);
		img.src = URL.createObjectURL(file);

		// Clean up the object URL to prevent memory leaks
		setTimeout(() => URL.revokeObjectURL(img.src), 1000);
	});
}

/**
 * Converts a data URI to a Blob
 * @param dataURI - The data URI to convert
 * @returns Blob - The converted blob
 */
export function dataURItoBlob(dataURI: string): Blob {
	// Convert base64/URLEncoded data component to raw binary data held in a string
	let byteString;
	if (dataURI.split(',')[0].indexOf('base64') >= 0) {
		byteString = atob(dataURI.split(',')[1]);
	} else {
		byteString = unescape(dataURI.split(',')[1]);
	}

	// Separate out the MIME component
	const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

	// Write the bytes of the string to a typed array
	const ia = new Uint8Array(byteString.length);
	for (let i = 0; i < byteString.length; i++) {
		ia[i] = byteString.charCodeAt(i);
	}

	return new Blob([ia], { type: mimeString });
}

/**
 * Resizes an image with timeout protection
 * @param originalFile - The original image file
 * @param maxWidth - Maximum width for resizing
 * @param maxHeight - Maximum height for resizing
 * @param compressFormat - Compression format (e.g., 'JPEG')
 * @param quality - Quality percentage (0-100)
 * @param rotation - Rotation angle in degrees
 * @param outputType - Output type ('base64' or 'blob')
 * @param timeoutMs - Timeout in milliseconds (default: 15000)
 * @returns Promise<string> - The resized image as data URI
 */
export function resizeImageWithTimeout(
	originalFile: File,
	maxWidth: number,
	maxHeight: number,
	compressFormat: string,
	quality: number,
	rotation: number,
	outputType: string,
	timeoutMs: number = 15000
): Promise<string> {
	return new Promise((resolve, reject) => {
		let timeoutHandle: NodeJS.Timeout | null = null;

		// Cleanup function to clear the timeout
		const cleanup = () => {
			if (timeoutHandle) {
				clearTimeout(timeoutHandle);
				timeoutHandle = null;
			}
		};

		// Set a timeout for the resizing operation
		timeoutHandle = setTimeout(() => {
			cleanup();
			reject(
				new Error(
					`Image resizing timed out after ${
						timeoutMs / 1000
					} seconds. The file might be corrupt or too complex.`
				)
			);
		}, timeoutMs);

		try {
			Resizer.imageFileResizer(
				originalFile,
				maxWidth,
				maxHeight,
				compressFormat,
				quality,
				rotation,
				(
					resizedOutput:
						| string
						| Blob
						| File
						| ProgressEvent<FileReader>
				) => {
					// Success callback
					cleanup();
					resolve(resizedOutput as string);
				},
				outputType
			);
		} catch (synchronousError) {
			// Catch synchronous errors thrown by Resizer.imageFileResizer during setup
			cleanup();
			reject(synchronousError);
		}
	});
}

/**
 * Processes an image file for upload: converts HEIC/HEIF to JPEG and resizes if needed
 * @param file - The original file to process
 * @param maxWidth - Maximum width for resizing (default: 1200)
 * @param maxHeight - Maximum height for resizing (default: 1200)
 * @param quality - Quality percentage for JPEG compression (default: 80)
 * @param sizeThreshold - File size threshold in bytes above which to resize (default: 100000 ~ 100KB)
 * @returns Promise<{processedFile: File, originalFile: File}> - The processed file and original file
 */
export async function processImageForUpload(
	file: File,
	maxWidth: number = 1200,
	maxHeight: number = 1200,
	quality: number = 60,
	sizeThreshold: number = 100000
): Promise<{ processedFile: File; originalFile: File }> {
	// Normalize HEIC files first
	let processedFile = normalizeHeicFile(file);

	// Convert HEIC/HEIF to JPEG if needed
	if (
		processedFile.type.includes('heic') ||
		processedFile.type.includes('heif')
	) {
		try {
			const convertedBlob = await heic2any({
				blob: processedFile,
				toType: 'image/jpeg',
				quality: 1, // Keep original quality for conversion
			});

			// Convert Blob to File
			processedFile = new File(
				[convertedBlob as Blob],
				file.name.replace(/\.(heic|heif)$/i, '.jpg'),
				{ type: 'image/jpeg' }
			);
		} catch (error) {
			console.error('Error converting HEIC file:', error);
			throw new Error(
				'Failed to convert HEIC image. Please try a different format.'
			);
		}
	}

	// Validate the image format
	const isValid = await isValidImageFormat(processedFile);
	if (!isValid) {
		throw new Error('File is not a valid image');
	}

	// Resize if file is larger than threshold
	if (processedFile.size > sizeThreshold) {
		try {
			const fileUri = await resizeImageWithTimeout(
				processedFile,
				maxWidth,
				maxHeight,
				'JPEG',
				quality,
				0, // No rotation
				'base64'
			);

			const resizedBlob = dataURItoBlob(fileUri);
			processedFile = new File(
				[resizedBlob],
				file.name.replace(/\.(heic|heif|png)$/i, '.jpg'),
				{ type: 'image/jpeg' }
			);
		} catch (error) {
			console.error('Error resizing image:', error);
			// If resizing fails, continue with original processed file
			// Don't throw here as the file is still valid
		}
	}

	return {
		processedFile,
		originalFile: file,
	};
}
